import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { HTTPService } from "../../../services/httpService";
import ScopeService from "../../../services/scopeService";
import { toast } from 'react-toastify';
import AddStaffModal from './AddStaffModal';
import ManagerCheckInModal from './ManagerCheckInModal';
import ManagerCheckOutModal from './ManagerCheckOutModal';
import TransferStaffModal from './TransferStaffModal';
import { getStaffTimeStatus } from '../../../services/timeTrackingService';
import {
    mapToDealerRole,
    UNIFIED_ROLES,
    DEALER_ROLES,
    getUnifiedRoleBadgeColor,
    getUnifiedRoleLabel
} from '../../../util/staffRoles';
import { convertFromUTC } from '../../../util/convert_timezone';
import {
    isMultiLocation,
    isAllLocations,
    getLocationName,
    getLocationBadgeColor
} from '../../../util/locationUtils';
import ColorPicker from '../../common/ColorPicker';
import ColorSettingsAPI from '../../../services/colorSettingsService';

const API = new HTTPService();

/**
 * StaffManagement - Manage staff for installer or dealer companies
 *
 * For Installers (Specialists):
 * - View all staff at their company
 * - Add new staff members (technicians or managers)
 * - Edit staff roles
 * - Deactivate staff accounts
 *
 * For Dealers:
 * - View all staff AND contacts (unified list)
 * - Add new staff members (sales, finance, manager)
 * - Upgrade contacts to staff (grant login access)
 * - Edit staff roles
 * - Deactivate staff accounts
 */
class StaffManagement extends Component {
  state = {
    staffList: [],
    loading: true,
    error: null,

    // Modal state
    showAddModal: false,
    editingStaff: null, // null = adding new, object = editing existing

    // Company info
    companyId: null,
    companyName: '',

    // Role filter
    roleFilter: 'all',

    // Time tracking state (installers only)
    timeTrackingEnabled: false, // Whether time tracking is enabled for this location
    timeTrackingStatus: {}, // Map of user_id -> time status
    timeTrackingLoading: false,

    // Manager check-in/check-out modals
    showCheckInModal: false,
    showCheckOutModal: false,
    selectedTechnician: null, // Technician being checked in/out
    checkActionLoading: null, // User ID currently being processed

    // Transfer modal state (multi-location admin/owner only)
    showTransferModal: false,
    transferStaff: null
  };

  async componentDidMount() {
    const { typeId, companyName, userType } = this.props;
    this.setState({ companyId: typeId, companyName });
    await this.loadStaff();

    // Load time tracking settings and status for installers only
    if (userType !== 'dealer') {
      const isEnabled = this.loadTimeTrackingSettings();
      // Only load status if time tracking is enabled
      if (isEnabled) {
        await this.loadTimeTrackingStatus();
      }
    }
  }

  // Load time tracking enabled setting from company data
  // Returns whether time tracking is enabled
  loadTimeTrackingSettings = () => {
    try {
      const companyData = ScopeService.getCompany() || {};
      // Time tracking mode: 'none' | 'voluntary' | 'required'
      // - none: feature disabled, hide UI
      // - voluntary/required: feature enabled, show UI
      const timeTrackingMode = companyData.time_tracking_mode || 'none';
      const timeTrackingEnabled = timeTrackingMode !== 'none';
      this.setState({ timeTrackingEnabled });
      return timeTrackingEnabled;
    } catch (error) {
      console.error('Error loading time tracking settings:', error);
      return false;
    }
  }

  loadStaff = async () => {
    try {
      this.setState({ loading: true, error: null });

      const { typeId, userType } = this.props;
      let staff = [];

      if (userType === 'dealer') {
        // Dealers: Use dealerContacts endpoint which returns all dealer users
        const { data } = await API.get(`/dealerContacts/${typeId}`);
        staff = data || [];
      } else {
        // Installers: Use users endpoint with filters
        const locationParam = this.props.selectedLocationId && this.props.selectedLocationId !== 'all'
          ? `&location_id=${this.props.selectedLocationId}` : '';
        const { data } = await API.get(`/users?company_id=${typeId}&user_type=installer${locationParam}`);

        staff = data || [];
      }

      this.setState({
        staffList: staff,
        loading: false
      });
    } catch (error) {
      console.error('Error loading staff:', error);
      this.setState({
        error: 'Failed to load staff list',
        loading: false
      });
    }
  };

  // Load time tracking status for all staff (installers only)
  loadTimeTrackingStatus = async () => {
    const { typeId, userType } = this.props;

    // Only load for installers
    if (userType === 'dealer') return;

    try {
      this.setState({ timeTrackingLoading: true });

      const statusData = await getStaffTimeStatus(typeId);

      // Convert array to map for easy lookup
      const statusMap = {};
      if (Array.isArray(statusData)) {
        statusData.forEach(item => {
          // Derive is_checked_in from session data - checked in if session exists and is not closed
          // Active statuses: 'open', 'flagged' (still working, just flagged for review)
          const activeStatuses = ['open', 'flagged'];
          const isCheckedIn = item.session_id && activeStatuses.includes(item.session_status);
          statusMap[item.user_id] = {
            is_checked_in: isCheckedIn,
            check_in_at: item.check_in_at,
            session_id: item.session_id,
            session_status: item.session_status
          };
        });
      }

      this.setState({
        timeTrackingStatus: statusMap,
        timeTrackingLoading: false
      });
    } catch (error) {
      console.error('Error loading time tracking status:', error);
      this.setState({ timeTrackingLoading: false });
    }
  };

  // Open check-in modal
  handleOpenCheckInModal = (staff) => {
    this.setState({
      showCheckInModal: true,
      selectedTechnician: staff
    });
  };

  // Open check-out modal
  handleOpenCheckOutModal = (staff) => {
    const { timeTrackingStatus } = this.state;
    const status = timeTrackingStatus[staff.User_id];

    this.setState({
      showCheckOutModal: true,
      selectedTechnician: {
        ...staff,
        check_in_at: status?.check_in_at
      }
    });
  };

  // Close check-in modal
  handleCloseCheckInModal = () => {
    this.setState({
      showCheckInModal: false,
      selectedTechnician: null
    });
  };

  // Close check-out modal
  handleCloseCheckOutModal = () => {
    this.setState({
      showCheckOutModal: false,
      selectedTechnician: null
    });
  };

  // Handle successful check-in
  handleCheckInSuccess = (technician) => {
    toast.success(`${technician.user_name} has been checked in.`);
    this.loadTimeTrackingStatus();
  };

  // Handle successful check-out
  handleCheckOutSuccess = (technician, durationMinutes) => {
    const duration = durationMinutes ? ` (${Math.round(durationMinutes)} minutes)` : '';
    toast.success(`${technician.user_name} has been checked out.${duration}`);
    this.loadTimeTrackingStatus();
  };

  // Get time tracking status for a staff member
  getTimeStatus = (userId) => {
    const { timeTrackingStatus } = this.state;
    return timeTrackingStatus[userId] || null;
  };

  // Format check-in time
  formatCheckInTime = (checkInAt) => {
    if (!checkInAt) return '-';
    return convertFromUTC(checkInAt).format('h:mm A');
  };

  handleAddStaff = () => {
    this.setState({
      showAddModal: true,
      editingStaff: null
    });
  };

  handleEditStaff = (staff) => {
    this.setState({
      showAddModal: true,
      editingStaff: staff
    });
  };

  handleCloseModal = () => {
    this.setState({
      showAddModal: false,
      editingStaff: null
    });
  };

  handleSaveStaff = async (staffData) => {
    try {
      const { typeId, userType } = this.props;
      const isEditing = !!this.state.editingStaff;

      if (isEditing) {
        // Update existing staff - include ALL required fields to prevent nulls
        // The backend replaces all fields, so we must send everything
        const existingStaff = this.state.editingStaff;

        await API.put(`/users/${existingStaff.User_id}`, {
          // Fields being updated
          userName: staffData.name,
          userPhoneNumber: staffData.phone || existingStaff.user_phone_number || '',
          role: staffData.role,  // NEW: Use 'role' instead of 'userRole'
          active: staffData.active ? 1 : 0,
          // Required fields that must be preserved
          email: existingStaff.Email,
          companyId: existingStaff.Company_id || typeId,
          userType: userType
        });
        alert('Staff member updated successfully!');
      } else {
        // Check if email already exists in staff list
        const existingStaff = this.state.staffList.find(
          s => s.Email && s.Email.toLowerCase() === staffData.email.toLowerCase()
        );
        if (existingStaff) {
          alert(`A user with email "${staffData.email}" already exists: ${existingStaff.user_name}`);
          return;
        }

        // Create new staff member
        const createData = {
          email: staffData.email,
          userName: staffData.name,
          userPhoneNumber: staffData.phone,
          companyId: typeId,
          userType: userType,
          role: staffData.role,
          active: 1,
          location_id: staffData.location_id
        };

        const { data: result } = await API.post('/users', createData);

        // Create Cognito account and send invite email (if requested)
        if (result.userId && staffData.createLogin) {
          try {
            await API.post('/auth/create-user', {
              email: staffData.email,
              userId: result.userId
            });
            alert('Staff member created! They will receive an email to set their password.');
          } catch (cognitoError) {
            console.error('Cognito account creation failed:', cognitoError);
            alert('Staff member created, but email invite failed. You may need to manually send a password reset.');
          }
        } else if (result.userId && !staffData.createLogin) {
          // Contact only (no login) - for dealers
          alert('Contact added successfully! They will receive notifications but cannot log in.');
        }
      }

      this.handleCloseModal();
      await this.loadStaff();

    } catch (error) {
      console.error('Error saving staff:', error);
      alert('Error saving staff member: ' + (error.response?.data?.message || error.message));
    }
  };


  handleColorChange = async (staff, color) => {
    try {
      await ColorSettingsAPI.updateUserColor(staff.User_id, color);
      // Update local state
      this.setState(prev => ({
        staffList: prev.staffList.map(s =>
          s.User_id === staff.User_id ? { ...s, user_color: color } : s
        )
      }));
    } catch (err) {
      console.error('Error updating user color:', err);
      toast.error('Failed to update color');
    }
  };

  handleDeactivateStaff = async (staff) => {
    if (!window.confirm(`Are you sure you want to deactivate ${staff.user_name}? They will no longer be able to log in.`)) {
      return;
    }

    try {
      await API.put(`/users/${staff.User_id}`, {
        active: 0
      });
      alert('Staff member deactivated.');
      await this.loadStaff();
    } catch (error) {
      console.error('Error deactivating staff:', error);
      alert('Error deactivating staff member.');
    }
  };

  handleReactivateStaff = async (staff) => {
    try {
      await API.put(`/users/${staff.User_id}`, {
        active: 1
      });
      alert('Staff member reactivated.');
      await this.loadStaff();
    } catch (error) {
      console.error('Error reactivating staff:', error);
      alert('Error reactivating staff member.');
    }
  };

  handleResendInvite = async (staff) => {
    try {
      await API.post('/auth/request-password-reset', {
        email: staff.Email
      });
      alert('Password reset email sent to ' + staff.Email);
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Error sending password reset email.');
    }
  };

  /**
   * Filter staff list by current user's role.
   * admin  → sees owner, manager, technician, viewer (NOT other admins)
   * owner  → sees manager, technician, viewer
   * manager → sees technician, viewer only
   * Current user always sees themselves.
   */
  getVisibleStaff = () => {
    const { staffList } = this.state;
    const { user, userType } = this.props;

    // Dealers see everyone (no role hierarchy filtering)
    if (userType === 'dealer') return staffList;

    const currentUserId = user?.User_id;
    const viewerRole = user?.role;

    const visibleByRole = {
      admin: ['owner', 'manager', 'technician', 'viewer'],
      owner: ['manager', 'technician', 'viewer'],
      manager: ['technician', 'viewer']
    };

    const allowed = visibleByRole[viewerRole];
    if (!allowed) return staffList; // Shouldn't reach here due to route guard

    return staffList.filter(s => {
      if (s.User_id === currentUserId) return true;
      const role = (s.role || '').toLowerCase();
      return allowed.includes(role);
    });
  };

  handleOpenTransferModal = (staff) => {
    this.setState({ showTransferModal: true, transferStaff: staff });
  };

  handleCloseTransferModal = () => {
    this.setState({ showTransferModal: false, transferStaff: null });
  };

  handleTransferSuccess = (staff) => {
    this.loadStaff();
  };

  getRoleBadge = (role, originalRole = null) => {
    const { userType } = this.props;
    const normalizedRole = role ? role.toLowerCase().trim() : '';

    // Check if it's a known role (installer or dealer)
    const roleSet = userType === 'dealer' ? DEALER_ROLES : UNIFIED_ROLES;
    const knownRole = roleSet.some(r => r.value === normalizedRole);

    if (knownRole) {
      return (
        <span className={`badge badge-${getUnifiedRoleBadgeColor(normalizedRole)}`}>
          {getUnifiedRoleLabel(normalizedRole, userType)}
        </span>
      );
    }

    // Legacy: try to map free-text dealer roles
    if (userType === 'dealer') {
      const mappedRole = mapToDealerRole(normalizedRole);
      if (mappedRole) {
        return (
          <span className={`badge badge-${getUnifiedRoleBadgeColor(mappedRole)}`}>
            {getUnifiedRoleLabel(mappedRole, 'dealer')}
          </span>
        );
      }
      return <span className="badge badge-secondary">{originalRole || role || 'Contact'}</span>;
    }

    // Legacy installer fallback
    return <span className="badge badge-secondary">{role || 'Staff'}</span>;
  };

  getStatusBadge = (active) => {
    return active === 1 || active === true
      ? <span className="badge badge-success">Active</span>
      : <span className="badge badge-secondary">Inactive</span>;
  };

  render() {
    const {
      loading, error, showAddModal, editingStaff, roleFilter,
      showCheckInModal, showCheckOutModal, selectedTechnician, timeTrackingEnabled, timeTrackingLoading,
      showTransferModal, transferStaff
    } = this.state;
    const { user, userType, typeId, locations, selectedLocationId } = this.props;
    const isDealer = userType === 'dealer';
    const isInstaller = userType !== 'dealer';

    // Role-based visibility filter
    const visibleStaff = this.getVisibleStaff();

    // Multi-location state
    const showLocationColumn = isInstaller && isMultiLocation(locations) && isAllLocations(selectedLocationId);
    const canTransfer = isInstaller && isMultiLocation(locations) && ['admin', 'owner'].includes(user?.role);
    const canManageColors = isInstaller && ['admin', 'owner', 'manager'].includes(user?.role);

    // Count staff by role using visible staff
    const getRoleValue = (s) => (s.role || '').toLowerCase();
    let roleCounts = {};

    if (isDealer) {
      // Dealer role counts
      DEALER_ROLES.forEach(r => {
        roleCounts[r.value] = visibleStaff.filter(s => {
          const role = getRoleValue(s);
          if (role === r.value) return true;
          // Map legacy free-text roles
          if (!role && r.value === 'contact') return true;
          return mapToDealerRole(role) === r.value;
        }).length;
      });
    } else {
      // Installer role counts
      UNIFIED_ROLES.forEach(r => {
        roleCounts[r.value] = visibleStaff.filter(s => getRoleValue(s) === r.value).length;
      });
    }
    roleCounts.all = visibleStaff.length;

    // Filter staff by role (starting from visible staff, not full list)
    let filteredStaff = visibleStaff;
    if (roleFilter !== 'all') {
      filteredStaff = visibleStaff.filter(s => {
        const role = getRoleValue(s);
        if (role === roleFilter) return true;
        // For dealers, also match legacy free-text roles
        if (isDealer) {
          if (!role && roleFilter === 'contact') return true;
          return mapToDealerRole(role) === roleFilter;
        }
        return false;
      });
    }

    return (
      <div className="container-fluid mt-4">
        <div className="row mb-4">
          <div className="col">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <select
                  className="form-control mr-3"
                  style={{ width: 'auto' }}
                  value={roleFilter}
                  onChange={(e) => this.setState({ roleFilter: e.target.value })}
                >
                  <option value="all">All Roles ({roleCounts.all})</option>
                  {(isDealer ? DEALER_ROLES : UNIFIED_ROLES).map(r => (
                    roleCounts[r.value] > 0 && (
                      <option key={r.value} value={r.value}>
                        {r.label}s ({roleCounts[r.value]})
                      </option>
                    )
                  ))}
                </select>
              </div>
              <button
                className="jv-btn jv-btn-primary"
                onClick={this.handleAddStaff}
              >
                <i className="fa-sharp fa-regular fa-plus" /> Add {isDealer ? 'Team Member' : 'Staff Member'}
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button
              className="btn btn-link"
              onClick={this.loadStaff}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading staff...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && visibleStaff.length === 0 && (
          <div className="text-center py-5">
            <i className="fa-sharp fa-regular fa-users fa-3x text-muted mb-3"></i>
            <h5>No Staff Members Yet</h5>
            <p className="text-muted">
              Add technicians and managers to assign work and track service completion.
            </p>
            <button
              className="btn btn-primary"
              onClick={this.handleAddStaff}
            >
              <i className="fa-sharp fa-regular fa-plus mr-2"></i>
              Add Your First Staff Member
            </button>
          </div>
        )}

        {/* No Results for Filter */}
        {!loading && !error && visibleStaff.length > 0 && filteredStaff.length === 0 && (
          <div className="text-center py-5">
            <p className="text-muted">No staff members match the selected filter.</p>
          </div>
        )}

        {/* Staff List */}
        {!loading && !error && filteredStaff.length > 0 && (
          <div className="card">
            <div className="table-responsive">
              <table className="jv-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    {isInstaller && <th>Color</th>}
                    {showLocationColumn && <th>Location</th>}
                    {isInstaller && timeTrackingEnabled && <th>Checked In</th>}
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.map(staff => {
                    const isCurrentUser = staff.User_id === user?.User_id;
                    const isActive = staff.active === 1 || staff.active === true;

                    return (
                      <tr key={staff.User_id} className={!isActive ? 'table-secondary' : ''}>
                        <td>
                          {staff.user_name}
                          {isCurrentUser && (
                            <span className="badge badge-info ml-2">You</span>
                          )}
                        </td>
                        <td>{staff.Email}</td>
                        <td>{staff.user_phone_number || '-'}</td>
                        <td>{this.getRoleBadge(staff.role, staff.contact_role)}</td>
                        {isInstaller && (
                          <td>
                            {canManageColors ? (
                              <ColorPicker
                                selectedColor={staff.user_color || null}
                                onSelect={(color) => this.handleColorChange(staff, color)}
                                onClear={() => this.handleColorChange(staff, null)}
                              />
                            ) : (
                              <div style={{
                                width: 20, height: 20, borderRadius: '50%',
                                background: staff.user_color || '#e5e7eb',
                                border: staff.user_color ? 'none' : '1px dashed #d1d5db',
                              }} />
                            )}
                          </td>
                        )}
                        {showLocationColumn && (() => {
                          const locId = staff.location_id || staff.locationId;
                          const loc = locations.find(l => String(l.location_id) === String(locId));
                          return (
                            <td>
                              {loc ? (
                                <span
                                  className="jv-location-badge"
                                  style={{ backgroundColor: getLocationBadgeColor(locId, locations) }}
                                >
                                  {getLocationName(loc)}
                                </span>
                              ) : '-'}
                            </td>
                          );
                        })()}
                        {/* Time Tracking Column - Installers only, when time tracking is enabled */}
                        {isInstaller && timeTrackingEnabled && (() => {
                          const timeStatus = this.getTimeStatus(staff.User_id);
                          const isCheckedIn = timeStatus?.is_checked_in;

                          return (
                            <td>
                              <div className="d-flex align-items-center">
                                {/* Check-in time display - only show when checked in */}
                                {isCheckedIn && (
                                  <span className="text-success mr-2">
                                    {this.formatCheckInTime(timeStatus.check_in_at)}
                                  </span>
                                )}

                                {/* Check In/Out Button */}
                                {isActive && (
                                  <button
                                    className={`btn btn-sm ${isCheckedIn ? 'btn-success' : 'btn-outline-secondary'}`}
                                    onClick={() => isCheckedIn
                                      ? this.handleOpenCheckOutModal(staff)
                                      : this.handleOpenCheckInModal(staff)
                                    }
                                    disabled={timeTrackingLoading}
                                    title={isCheckedIn ? 'Click to check out' : 'Click to check in'}
                                  >
                                    {timeTrackingLoading ? (
                                      <span className="spinner-border spinner-border-sm"></span>
                                    ) : isCheckedIn ? (
                                      <>
                                        <i className="fa-sharp fa-regular fa-sign-out mr-1"></i>
                                        <span className="d-none d-md-inline">Check Out</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fa-sharp fa-regular fa-sign-in mr-1"></i>
                                        <span className="d-none d-md-inline">Check In</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })()}
                        <td>{this.getStatusBadge(staff.active)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                            {/* Edit */}
                            <button
                              className="jv-btn-icon"
                              onClick={() => this.handleEditStaff(staff)}
                              title="Edit staff member"
                            >
                              <i className="fa-sharp fa-regular fa-pen-to-square"></i>
                            </button>

                            {/* Time History - Installers only */}
                            {isInstaller && (
                              <Link
                                to={`/staff/${staff.User_id}/time`}
                                className="jv-btn-icon"
                                title="View time history"
                              >
                                <i className="fa-sharp fa-regular fa-clock-rotate-left"></i>
                              </Link>
                            )}

                            {/* Transfer - admin/owner only, multi-location */}
                            {canTransfer && !isCurrentUser && isActive && (
                              <button
                                className="jv-btn-icon"
                                onClick={() => this.handleOpenTransferModal(staff)}
                                title="Transfer to another location"
                              >
                                <i className="fa-sharp fa-regular fa-arrow-right-arrow-left"></i>
                              </button>
                            )}

                            {/* Deactivate/Reactivate */}
                            {!isCurrentUser && (
                              isActive ? (
                                <button
                                  className="jv-btn-icon jv-btn-icon-danger"
                                  onClick={() => this.handleDeactivateStaff(staff)}
                                  title="Deactivate staff member"
                                >
                                  <i className="fa-sharp fa-regular fa-ban"></i>
                                </button>
                              ) : (
                                <button
                                  className="jv-btn-icon jv-btn-icon-success"
                                  onClick={() => this.handleReactivateStaff(staff)}
                                  title="Reactivate staff member"
                                >
                                  <i className="fa-sharp fa-regular fa-rotate-right"></i>
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        <AddStaffModal
          show={showAddModal}
          onHide={this.handleCloseModal}
          onSave={this.handleSaveStaff}
          onResendInvite={this.handleResendInvite}
          editingStaff={editingStaff}
          userType={userType}
          userRole={user?.role}
          selectedLocationId={selectedLocationId}
        />

        {/* Transfer Modal - admin/owner only, multi-location */}
        {canTransfer && (
          <TransferStaffModal
            show={showTransferModal}
            onHide={this.handleCloseTransferModal}
            onSuccess={this.handleTransferSuccess}
            staff={transferStaff}
            locations={locations}
            companyId={typeId}
          />
        )}

        {/* Manager Check-In Modal - Installers only, when time tracking is enabled */}
        {isInstaller && timeTrackingEnabled && (
          <ManagerCheckInModal
            show={showCheckInModal}
            onHide={this.handleCloseCheckInModal}
            onSuccess={this.handleCheckInSuccess}
            technician={selectedTechnician}
            locationId={typeId}
            actorUserId={user?.User_id}
          />
        )}

        {/* Manager Check-Out Modal - Installers only, when time tracking is enabled */}
        {isInstaller && timeTrackingEnabled && (
          <ManagerCheckOutModal
            show={showCheckOutModal}
            onHide={this.handleCloseCheckOutModal}
            onSuccess={this.handleCheckOutSuccess}
            technician={selectedTechnician}
            locationId={typeId}
            actorUserId={user?.User_id}
          />
        )}
      </div>
    );
  }
}

export default StaffManagement;
